import pdfMake from 'pdfmake/build/pdfmake';
import type {TDocumentDefinitions} from 'pdfmake/interfaces';
import type {ColDef, Column, GridApi, IRowNode} from 'ag-grid-community';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import Translator from "@/utils/common/Translator.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";

pdfMake.vfs = pdfFonts.vfs;

pdfMake.fonts = {
  Cairo: {
    normal: 'https://cdn.jsdelivr.net/npm/@expo-google-fonts/cairo@0.2.0/Cairo_400Regular.ttf',
    bold: 'https://cdn.jsdelivr.net/npm/@expo-google-fonts/cairo@0.2.0/Cairo_700Bold.ttf',
    italics: 'https://cdn.jsdelivr.net/npm/@expo-google-fonts/cairo@0.2.0/Cairo_400Regular.ttf',
    bolditalics: 'https://cdn.jsdelivr.net/npm/@expo-google-fonts/cairo@0.2.0/Cairo_700Bold.ttf'
  },
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-MediumItalic.ttf'
  }
};

const PDF_CONSTANTS = {
  PAGE_ORIENTATION: 'landscape' as const,
  PAGE_MARGINS: [10, 10, 10, 10] as [number, number, number, number],
  HEADER_ROW_HEIGHT: 40,
  DATA_ROW_HEIGHT: 15,
  COLORS: {
    HEADER_ROW: '#f8f8f8',
    EVEN_ROW: '#fcfcfc',
    ODD_ROW: '#fff',
    INNER_BORDER: '#dde2eb',
    OUTER_BORDER: '#babfc7',
  },
  HEADER_MARGIN: [0, 12, 0, 0] as [number, number, number, number],
} as const;

interface CellContent {
  text: string;
  bold?: boolean;
  margin?: [number, number, number, number];

  [key: string]: any;
}

interface TableLayout {
  fillColor: (rowIndex: number) => string;
  vLineWidth: (rowIndex: number, node: any) => number;
  hLineColor: (rowIndex: number, node: any) => string;
  vLineColor: (rowIndex: number, node: any) => string;
}

interface ExportOptions {
  fileName?: string;
  pageOrientation?: 'portrait' | 'landscape';
  includeSort?: boolean;
  font?: 'Cairo' | 'Roboto';
}

class GridExporter {
  private static instance: GridExporter;

  private constructor() {
  }

  public static getInstance(): GridExporter {
    if (!GridExporter.instance) {
      GridExporter.instance = new GridExporter();
    }
    return GridExporter.instance;
  }

  public exportToPDF(gridApi: GridApi | undefined, options?: ExportOptions): void {
    this.validateGridApi(gridApi);
    try {
      const documentDefinition = this.createDocumentDefinition(gridApi, options);
      const fileName = options?.fileName || 'grid-export.pdf';
      pdfMake.createPdf(documentDefinition).download(fileName);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      throw new Error('PDF export failed. Please check console for details.');
    }
  }

  private createDocumentDefinition(gridApi: GridApi, options?: ExportOptions): TDocumentDefinitions {
    const columns: Column[] = gridApi.getAllDisplayedColumns().filter((col: Column) => col.getColId() !== 'ag-Grid-SelectionColumn');
    const headerRow = this.buildHeaderRow(gridApi, columns, options?.includeSort);
    const dataRows = this.buildDataRows(gridApi, columns);
    const columnWidth = `${97 / (columns.length - 1)}%`;
    let fill = Array(columns.length - 1).fill(columnWidth);
    fill.unshift("3%");
    return {
      pageOrientation: options?.pageOrientation || PDF_CONSTANTS.PAGE_ORIENTATION,
      pageMargins: PDF_CONSTANTS.PAGE_MARGINS,
      defaultStyle: {
        font: options?.font || Translator.isArabic() ? 'Cairo' : 'Roboto',
        fontSize: 10
      },
      content: [
        {
          alignment: 'center',
          table: {
            headerRows: 1,
            widths: fill,
            body: [headerRow, ...dataRows],
            heights: (rowIndex: number) => this.getRowHeight(rowIndex)
          },
          layout: this.createTableLayout(1),
        },
      ],
    };
  }

  private createTableLayout(numberOfHeaderRows: number): TableLayout {
    return {
      fillColor: (rowIndex: number): string => {
        if (rowIndex < numberOfHeaderRows) {
          return PDF_CONSTANTS.COLORS.HEADER_ROW;
        }
        return rowIndex % 2 === 0
          ? PDF_CONSTANTS.COLORS.EVEN_ROW
          : PDF_CONSTANTS.COLORS.ODD_ROW;
      },

      vLineWidth: (rowIndex: number, node: any): number => {
        const isOuterBorder = rowIndex === 0 || rowIndex === node.table.widths.length;
        return isOuterBorder ? 1 : 0;
      },

      hLineColor: (rowIndex: number, node: any): string => {
        const isOuterBorder = rowIndex === 0 || rowIndex === node.table.body.length;
        return isOuterBorder
          ? PDF_CONSTANTS.COLORS.OUTER_BORDER
          : PDF_CONSTANTS.COLORS.INNER_BORDER;
      },

      vLineColor: (rowIndex: number, node: any): string => {
        const isOuterBorder = rowIndex === 0 || rowIndex === node.table.widths.length;
        return isOuterBorder
          ? PDF_CONSTANTS.COLORS.OUTER_BORDER
          : PDF_CONSTANTS.COLORS.INNER_BORDER;
      },
    };
  }

  private buildHeaderRow(gridApi: GridApi, columns: Column[], includeSort = true): CellContent[] {
    return columns.map((column: Column) => {
      const colDef = column.getColDef();
      const headerName = this.getHeaderName(colDef);
      const sort = includeSort ? column.getSort() : null;
      const sortIndicator = sort ? ` (${sort})` : '';
      return {
        text: StringUtils.reverseTextIfArabic(`${headerName}${sortIndicator}`),
        bold: true,
        margin: PDF_CONSTANTS.HEADER_MARGIN,
      };
    });
  }

  private getHeaderName(colDef: ColDef): string {
    const headerName = colDef.headerName ?? colDef.field ?? '';
    return this.capitalizeFirstLetter(headerName);
  }

  private buildDataRows(gridApi: GridApi, columns: Column[]): CellContent[][] {
    return gridApi.isPivotMode() ? this.buildPivotModeRows(gridApi, columns) : this.buildNormalModeRows(gridApi, columns);
  }

  private buildNormalModeRows(gridApi: GridApi, columns: Column[]): CellContent[][] {
    const rows: CellContent[][] = [];
    this.iterateVisibleNodes(gridApi, (node: IRowNode) => {
      const row = columns.map((column: Column) =>
        this.createCellContent(gridApi, column, node)
      );
      rows.push(row);
    });
    return rows;
  }

  private buildPivotModeRows(gridApi: GridApi, columns: Column[]): CellContent[][] {
    const rows: CellContent[][] = [];
    this.iterateVisibleNodes(gridApi, (node: IRowNode) => {
      if (!node.group) return;
      const row = columns.map((column: Column) =>
        this.createCellContent(gridApi, column, node)
      );
      rows.push(row);
    });
    return rows;
  }

  private iterateVisibleNodes(gridApi: GridApi, callback: (node: IRowNode) => void) {
    if (gridApi.getGridOption("rowModelType") === 'clientSide') {
      gridApi.forEachNodeAfterFilterAndSort(callback);
    } else {
      const pageSize = gridApi.paginationGetPageSize();
      const currentPage = gridApi.paginationGetCurrentPage();
      const startRow = currentPage * pageSize;
      const endRow = startRow + pageSize;
      for (let i = startRow; i < endRow; i++) {
        const node = gridApi.getDisplayedRowAtIndex(i);
        if (node)
          callback(node);
      }
    }
  }

  private createCellContent(gridApi: GridApi, column: Column, node: IRowNode): CellContent {
    let value = gridApi.getCellValue({rowNode: node, colKey: column.getColId()});
    const colDef = column.getColDef();
    if (value instanceof Object)
      value = Translator.translateRef(value);
    let text = StringUtils.reverseTextIfArabic(value?.toString() ?? '');
    return {
      text: text,
      ...colDef.cellStyle,
    };
  }

  private getRowHeight(rowIndex: number): number {
    return rowIndex === 0 ? PDF_CONSTANTS.HEADER_ROW_HEIGHT : PDF_CONSTANTS.DATA_ROW_HEIGHT;
  }

  private capitalizeFirstLetter(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private validateGridApi(gridApi: GridApi | null | undefined): asserts gridApi is GridApi {
    if (!gridApi) {
      throw new Error('GridApi is required for export operation');
    }
  }
}

export default GridExporter.getInstance();
